import type { DtActionsAtom } from '@/components/pages/backoffice/_klb-penyuluh/_kecamatan/state/atoms'
import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { KegiatanDataType } from './types'
import type { MutationKegiatanValues } from './validations'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['kegiatan-bpp', key] as const

const getAll = (
  params?: DtActionsAtom
): ServiceQueryOptionType<ServiceGetAllResponse<Array<KegiatanDataType>>> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KegiatanDataType>>
      >(`kelembagaan/bpp/${params?.id_bpp}/kegiatan`, {
        params: {
          page: params?.page,
          per_page: params?.perPage || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string,
  id_bpp?: string
): ServiceQueryOptionType<ServiceGetByIdResponse<KegiatanDataType>> => ({
  queryKey: QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<KegiatanDataType>>(
        `kelembagaan/bpp/${id_bpp ?? ''}/kegiatan/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KegiatanDataType>,
  MutationKegiatanValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationKegiatanValues) => {
    try {
      const response = await api.post(
        `kelembagaan/bpp/${data.id_bpp ?? ''}/kegiatan`,
        {
          ...data,
          id_fasilitasi_bpp: data.id_fasilitasi_bpp?.value
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KegiatanDataType>,
  MutationKegiatanValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationKegiatanValues) => {
    try {
      const response = await api.put<ServiceGetByIdResponse<KegiatanDataType>>(
        `kelembagaan/bpp/${data?.id_bpp ?? ''}/kegiatan/${data.id_kegiatan}`,
        {
          ...data,
          id_fasilitasi_bpp: data.id_fasilitasi_bpp?.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KegiatanDataType>,
  { id: string; id_bpp: string }
> => ({
  mutationFn: async (data: { id: string; id_bpp: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<KegiatanDataType>
      >(`kelembagaan/bpp/${data?.id_bpp ?? ''}/kegiatan/${data.id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const kegiatan = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData
}
